/**
 * Face Search — API client.
 *
 * WIRE (one line): if the app keeps its JWT under a different localStorage key than "token",
 * change TOKEN_KEYS below (or point `authHeaders` at the app's existing axios instance).
 */
import axios from "axios";

export const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = `${BACKEND_URL}/api`;
const TOKEN_KEYS = ["token", "access_token", "authToken", "jwt"];

export function authHeaders() {
  for (const k of TOKEN_KEYS) {
    const t = localStorage.getItem(k);
    if (t) return { Authorization: `Bearer ${t.replace(/^"|"$/g, "")}` };
  }
  return {};
}

const admin = axios.create({ baseURL: `${API}/face`, withCredentials: true }); // app uses an httpOnly cookie; Bearer is a fallback
admin.interceptors.request.use((cfg) => {
  cfg.headers = { ...(cfg.headers || {}), ...authHeaders() };
  return cfg;
});

export function errMsg(e, fallback = "Something went wrong. Please try again.") {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.message || fallback;
}

/** Turn any relative "/api/..." URL from the backend into an absolute one. */
export function abs(url) {
  if (!url) return url;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

export const faceAdmin = {
  config: () => admin.get("/config").then((r) => r.data),
  list: () => admin.get("/events").then((r) => r.data),
  get: (id) => admin.get(`/events/${id}`).then((r) => r.data),
  create: (body) => admin.post("/events", body).then((r) => r.data),
  update: (id, body) => admin.patch(`/events/${id}`, body).then((r) => r.data),
  start: (id) => admin.post(`/events/${id}/start`).then((r) => r.data),
  pause: (id) => admin.post(`/events/${id}/pause`).then((r) => r.data),
  resume: (id) => admin.post(`/events/${id}/resume`).then((r) => r.data),
  retryFailed: (id) => admin.post(`/events/${id}/retry-failed`).then((r) => r.data),
  sync: (id) => admin.post(`/events/${id}/sync`).then((r) => r.data),
  remove: (id) => admin.delete(`/events/${id}`).then((r) => r.data),
  photos: (id, params) => admin.get(`/events/${id}/photos`, { params }).then((r) => r.data),
  guests: (id) => admin.get(`/events/${id}/guests`).then((r) => r.data),
  guestsCsv: (id) => admin.get(`/events/${id}/guests.csv`, { responseType: "blob" }).then((r) => r.data),
  qr: (id) => admin.get(`/events/${id}/qr`, { responseType: "blob" }).then((r) => r.data),
  brands: () => admin.get("/brands").then((r) => r.data),
  updateBrand: (brand, body) => admin.put(`/brands/${brand}`, body).then((r) => r.data),
  uploadLogo: (brand, file) => {
    const fd = new FormData();
    fd.append("logo", file, file.name || "logo.png");
    return admin.post(`/brands/${brand}/logo`, fd).then((r) => r.data);
  },
  deleteLogo: (brand) => admin.delete(`/brands/${brand}/logo`).then((r) => r.data),
  byTransfer: (ref) => admin.get(`/events/by-transfer/${encodeURIComponent(ref)}`).then((r) => r.data),
  fromTransfer: (ref) => admin.post(`/events/from-transfer/${encodeURIComponent(ref)}`).then((r) => r.data),
};

export const facePublic = {
  event: (slug) => axios.get(`${API}/face/public/${slug}`).then((r) => r.data),
  guest: (slug, body) => axios.post(`${API}/face/public/${slug}/guest`, body).then((r) => r.data),
  search: (slug, file, guestId, pin) => {
    const fd = new FormData();
    fd.append("selfie", file, "selfie.jpg");
    if (guestId) fd.append("guest_id", guestId);
    if (pin) fd.append("pin", pin);
    return axios.post(`${API}/face/public/${slug}/search`, fd, { timeout: 60000 }).then((r) => r.data);
  },
  results: (token) => axios.get(`${API}/face/public/results/${token}`).then((r) => r.data),
  browse: (slug, page, pin) =>
    axios.get(`${API}/face/public/${slug}/photos`, { params: { page }, headers: pin ? { "X-Pin": pin } : {} }).then((r) => r.data),
  zipUrl: (slug) => `${API}/face/public/${slug}/zip`,
  forTransfer: (ref) => axios.get(`${API}/face/public/for-transfer/${encodeURIComponent(ref)}`).then((r) => r.data),
};

/** Trigger a browser-native (streamed to disk) ZIP download via a hidden form post. */
export function downloadZip(slug, { token, fileIds }) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = facePublic.zipUrl(slug);
  form.style.display = "none";
  const add = (n, v) => {
    const i = document.createElement("input");
    i.type = "hidden";
    i.name = n;
    i.value = v;
    form.appendChild(i);
  };
  if (token) add("token", token);
  if (fileIds && fileIds.length) add("file_ids", fileIds.join(","));
  document.body.appendChild(form);
  form.submit();
  setTimeout(() => form.remove(), 2000);
}

export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/** Downscale a selfie in the browser (fast upload, any phone format → JPEG). */
export function resizeImageFile(file, maxSide = 1200, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Could not process image"))), "image/jpeg", quality);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      // Some formats (e.g. HEIC on desktop) can't be decoded — send the original and let the server try.
      resolve(file);
    };
    img.src = url;
  });
}

export const BRAND = {
  wittyshadows: { label: "Wittyshadows", accent: "#f5a623" },
  triobliss: { label: "Trio Bliss", accent: "#f5a623" },
};
